/**
 * Event types that can be tracked across the application
 */
export enum EventType {
  PAGE_VIEW = "page_view",
  BUTTON_CLICK = "button_click",
  FORM_SUBMISSION = "form_submission",
  PRODUCT_VIEW = "product_view",
  ADD_TO_CART = "add_to_cart",
  REMOVE_FROM_CART = "remove_from_cart",
  CHECKOUT_STARTED = "checkout_started",
  CHECKOUT_INITIATED = "checkout_initiated",
  CHECKOUT_COMPLETED = "checkout_completed",
  SEARCH = "search",
  FILTER_APPLIED = "filter_applied",
  NEWSLETTER_SUBSCRIPTION = "newsletter_subscription",
  USER_SIGNUP = "user_signup",
  USER_LOGIN = "user_login",
  CUSTOM = "custom",
  USER_ENGAGEMENT = "user_engagement",
  SESSION_START= "session_start",
  FIRST_VISIT = "first_visit",
  SCROLL = "scroll",
  FORM_START= "form_start",
  ADD_PAYMENT_INFO = "add_payment_info",
  ORDER_PLACED = "order_placed", 
}

export enum TrackEventKpActionType {
  CLICK = "click",
  AUTOMATED = "automated",
}

/**
 * Base event interface that all events should implement
 */
export interface BaseEvent {
  type: EventType;
  timestamp?: number;
}

/**
 * Page view event
 */
export interface IPageViewEvent extends BaseEvent {
  type: EventType.PAGE_VIEW;
  path: string;
  title: string;
  referrer?: string;
}

/**
 * User engagement event
 */
export interface IUserEngagementEvent extends BaseEvent {
  type: EventType.USER_ENGAGEMENT;
  engagementTime?: number; // in seconds
  pagePath?: string;
  pageTitle?: string;
  scrollDepth?: number; // in percentage
  formId?: string;
  formName?: string;
  formFields?: Array<{
    fieldName: string;
    fieldValue: string;
  }>;
  buttonId?: string;
  buttonText?: string;
  location?: string;
  customProperties?: Record<string, any>;
}

/**
 * Session start event
 */
export interface ISessionStartEvent extends BaseEvent {
  type: EventType.SESSION_START;
  sessionId: string;
  userId?: string;
  userAgent?: string;
  referrer?: string;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}

/**
 * First visit event
 */
export interface IFirstVisitEvent extends BaseEvent {
  type: EventType.FIRST_VISIT;
  userId?: string;
  userAgent?: string;
  referrer?: string;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * Scroll event
 */
export interface IScrollEvent extends BaseEvent {
  type: EventType.SCROLL;
  scrollDepth: number; // in percentage
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * Form start event
 */
export interface IFormStartEvent extends BaseEvent {
  type: EventType.FORM_START;
  formId: string;
  formName?: string;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * Form submission event
 */
export interface IFormSubmissionEvent extends BaseEvent {
  type: EventType.FORM_SUBMISSION;
  formId: string;
  formName?: string;
  success: boolean;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * User signup event
 */
export interface IUserSignupEvent extends BaseEvent {
  type: EventType.USER_SIGNUP;
  userId?: string;
  method?: string;
  success: boolean;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * User login event
 */
export interface IUserLoginEvent extends BaseEvent {
  type: EventType.USER_LOGIN;
  userId?: string;
  method?: string;
  success: boolean;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * Button click event
 */
/**
 * Button click event
 */
export interface IButtonClickEvent extends BaseEvent {
  type: EventType.BUTTON_CLICK;
  buttonId?: string;
  buttonText?: string;
  location: string;
}

/**
 * Form submission event
 */
export interface IFormSubmissionEvent extends BaseEvent {
  type: EventType.FORM_SUBMISSION;
  formId: string;
  formName?: string;
  success: boolean;
}

/**
 * Product view event
 */
export interface IProductViewEvent extends BaseEvent {
  type: EventType.PRODUCT_VIEW;
  productId: string;
  productName: string;
  price?: number;
  currency?: string;
  category?: string;
}

/**
 * Add to cart event
 */
export interface IAddToCartEvent extends BaseEvent {
  type: EventType.ADD_TO_CART;
  productId: string;
  productName: string;
  price: number;
  currency?: string;
  quantity?: number;
  variant?: string;
}

/**
 * Remove from cart event
 */
export interface IRemoveFromCartEvent extends BaseEvent {
  type: EventType.REMOVE_FROM_CART;
  productId: string;
  productName: string;
  price: number;
  currency?: string;
  quantity: number;
  variant?: string;
}

/**
 * Checkout started event
 */
export interface ICheckoutStartedEvent extends BaseEvent {
  type: EventType.CHECKOUT_STARTED;
  cartValue: number;
  currency?: string;
  itemCount: number;
  items: Array<{
    productId: string;
    productName: string;
    price: number;
    quantity: number;
    variant?: string;
  }>;
}

/**
 * Checkout completed event
 */
export interface ICheckoutCompletedEvent extends BaseEvent {
  type: EventType.CHECKOUT_COMPLETED;
  orderId: string;
  cartValue: number;
  currency?: string;
  itemCount: number;
  items: Array<{
    productId: string;
    productName: string;
    price: number;
    quantity: number;
    variant?: string;
  }>;
}

/**
 * Search event
 */
export interface ISearchEvent extends BaseEvent {
  type: EventType.SEARCH;
  searchTerm: string;
  content_ids:string[];
  resultsCount?: number;
}

/**
 * Filter applied event
 */
export interface IFilterAppliedEvent extends BaseEvent {
  type: EventType.FILTER_APPLIED;
  filterType: string;
  filterValue: string | number | boolean;
}

/**
 * Newsletter subscription event
 */
export interface INewsletterSubscriptionEvent extends BaseEvent {
  type: EventType.NEWSLETTER_SUBSCRIPTION;
  email: string;
  success: boolean;
}

/**
 * User signup event
 */
export interface IUserSignupEvent extends BaseEvent {
  type: EventType.USER_SIGNUP;
  userId?: string;
  method?: string;
  success: boolean;
}

/**
 * User login event
 */
export interface IUserLoginEvent extends BaseEvent {
  type: EventType.USER_LOGIN;
  userId?: string;
  method?: string;
  success: boolean;
}

/**
 * Custom event for any other tracking needs
 */
export interface ICustomEvent extends BaseEvent {
  type: EventType.CUSTOM;
  name: string;
  properties?: Record<string, any>;
}

/**
 * Add Payment Info event
 */
export interface IAddPaymentInfoEvent extends BaseEvent {
  type: EventType.ADD_PAYMENT_INFO;
  cartValue: number;
  currency?: string;
  itemCount: number;
  paymentType?: string;
  items: Array<{
    productId: string;
    productName: string;
    price: number;
    quantity: number;
    variant?: string;
  }>;
}

/**
 * Union type of all possible events
 */
export type TEvent =
  | IPageViewEvent
  | IButtonClickEvent
  | IFormSubmissionEvent
  | IProductViewEvent
  | IAddToCartEvent
  | IRemoveFromCartEvent
  | ICheckoutStartedEvent
  | ICheckoutCompletedEvent
  | ISearchEvent
  | IFilterAppliedEvent
  | INewsletterSubscriptionEvent
  | IUserSignupEvent
  | IUserLoginEvent
  | ICustomEvent
  | IUserEngagementEvent
  | ISessionStartEvent
  | IFirstVisitEvent
  | IScrollEvent
  | IFormStartEvent
  | IAddPaymentInfoEvent;
